/**
 * Bounce kit — SSR-safe scroll reveal.
 *
 * 1. Put REVEAL_GATE_SCRIPT inline in <head> (index.html for vite-react-ssg,
 *    Base.astro for Astro). It hides [data-reveal] only once JS is known to run,
 *    and un-hides everything if initReveal() never runs within 2.5s.
 * 2. Call initReveal() once on the client (React: useEffect in the root layout;
 *    Astro: a <script> in the layout). It watches every [data-reveal] element,
 *    including ones added later (route changes, tab switches).
 */
export const REVEAL_GATE_SCRIPT =
  "(function(){var d=document.documentElement;d.classList.add('js-reveal');setTimeout(function(){if(!window.__revealReady)d.classList.remove('js-reveal')},2500)})();";

export function initReveal(): () => void {
  if (typeof window === "undefined") return () => {};
  (window as unknown as { __revealReady?: boolean }).__revealReady = true;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        el.classList.add("is-in");
        io.unobserve(el);
        // Drop the reveal transition afterwards so hover effects don't inherit
        // its stagger delay and springy overshoot.
        window.setTimeout(() => el.classList.add("reveal-done"), 1400);
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
  );

  const scan = (root: ParentNode) =>
    root.querySelectorAll("[data-reveal]:not(.is-in)").forEach((el) => io.observe(el));
  scan(document);

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches("[data-reveal]:not(.is-in)")) io.observe(node);
        scan(node);
      });
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  return () => {
    io.disconnect();
    mo.disconnect();
  };
}
